import { Stack, Text, textVariants, vars } from '@etg/wings';
import { Circle } from '@phosphor-icons/react';
import { css } from '@eti/styles';
import { useDateFormat } from '@eti/providers';
import { date } from '@eti/utils';
import TimelineWrapper from '../../../common/TimelineWrapper';
import TimelineRow from '../common/TimelineRow';
import Time from '../../../../../../common/time/Time';

const originalTimeStyles = css`
  color: #737373;
  text-decoration: line-through;
`;

const timeStyles = (isTimeChanged: boolean) => css`
  background-color: ${isTimeChanged ? vars.colors.signal.caution.tint : 'none'};
  width: fit-content;
`;

interface ItineraryLocationTimeProps {
  airportCode: string;
  airportName: string;
  cityName: string;
  dataTestIdPrefix: string;
  isLastStop?: boolean;
  isStartingPoint?: boolean;
  originalTime?: string | null;
  segmentId: string;
  time: string;
}

const ItineraryLocationTime = ({
  airportCode,
  airportName,
  cityName,
  dataTestIdPrefix,
  isLastStop,
  isStartingPoint,
  originalTime,
  segmentId,
  time,
}: ItineraryLocationTimeProps) => {
  const { time: timeFormat } = useDateFormat();

  const borderColorBefore = isStartingPoint
    ? vars.colors.inputs.border
    : vars.colors.decoration.dark;
  const borderColorAfter = isStartingPoint
    ? vars.colors.decoration.dark
    : vars.colors.inputs.border;
  const borderStyleBefore = isStartingPoint ? 'dashed' : 'solid';
  const borderStyleAfter = isStartingPoint ? 'solid' : 'dashed';
  const isTimeChanged = Boolean(originalTime) && time !== originalTime;

  const formattedTime = date.format(time, timeFormat);
  const formattedOriginalTime = originalTime ? date.format(originalTime, timeFormat) : null;

  return (
    <TimelineRow
      aria-labelledby={`itinerary-location-time-${segmentId}-${airportCode}`}
      data-testid="itinerary-location-time"
      id={`itinerary-location-time-${segmentId}-${airportCode}`}
      role="group"
    >
      <TimelineWrapper
        borderColorAfter={borderColorAfter}
        borderColorBefore={borderColorBefore}
        borderStyleAfter={isLastStop ? '' : borderStyleAfter}
        borderStyleBefore={borderStyleBefore}
        childSize={10}
      >
        <Circle
          aria-hidden
          color={vars.colors.decoration.dark}
          data-testid={`${dataTestIdPrefix}-circle`}
          size={10}
          weight="fill"
          width={21}
        />
      </TimelineWrapper>
      <Stack spacing={0}>
        <Stack spacing={4}>
          <Text
            as={Time}
            className={timeStyles(isTimeChanged)}
            data-testid={`${dataTestIdPrefix}-time`}
            date={time}
            format="time"
            variant={textVariants._16_BOLD}
          >
            {formattedTime}
          </Text>
          {isTimeChanged && originalTime && (
            <Text
              as={Time}
              className={originalTimeStyles}
              data-testid={`${dataTestIdPrefix}-original-time`}
              date={originalTime}
              format="time"
              variant={textVariants._16_BOLD}
            >
              {formattedOriginalTime}
            </Text>
          )}
          <Text data-testid={`${dataTestIdPrefix}-city`} variant={textVariants._16_BOLD}>
            {cityName}
          </Text>
        </Stack>
        <Text data-testid={`${dataTestIdPrefix}-airportName`}>
          {airportName} ({airportCode})
        </Text>
      </Stack>
    </TimelineRow>
  );
};

export default ItineraryLocationTime;
